import { useEffect, useState } from "react";
import type { OverlayConfig, OverlayControlGroup, OverlayElementInstance, OverlayElementType } from "@/models/overlayConfig";
import { OVERLAY_ELEMENT_TYPE_KEYS, OVERLAY_ELEMENT_TYPES, normalizeWebSourceURL, bugReadinessIssue, lowerThirdReadinessIssue, podiumVisibleSlots, pylonAthleteRows, tickerHasContent, advancedTimerHasContent } from "@/models/overlayConfig";
import { apiBaseUrl } from "@/firebase/app";
import { apiRequest } from "./functionsClient";

/** Dynamic control key: "{elementType}:{instanceId}" — see functions/src/overlayRuntime.ts. */
export type OverlayRuntimeControlKey = string;
export type OverlayRuntimeControls = Record<string, boolean>;
export type OverlayProgramStatus = "confirmed" | "outOfSync" | "offline";

export function overlayControlKey(type: OverlayElementType, instanceId: string): OverlayRuntimeControlKey {
  return `${type}:${instanceId}`;
}

export interface OverlayRuntimeState {
  overlayId: string;
  /** Effective (container-gated) controls — what the program renders. */
  controls: OverlayRuntimeControls;
  /** Ungated per-control state; optional until functions are redeployed. */
  rawControls?: OverlayRuntimeControls;
  configUpdatedAt: number | null;
  program: {
    status: OverlayProgramStatus;
    lastSeenAt: number | null;
    appliedControls: OverlayRuntimeControls | null;
  };
}

/**
 * Container AND gate: member control key → its container's `active` flag.
 * First container claiming a key wins (parseControlGroups already dedupes).
 */
function controlGroupGateMap(controlGroups: OverlayControlGroup[]): Record<string, boolean> {
  const gate: Record<string, boolean> = {};
  for (const group of controlGroups) {
    for (const member of group.members) {
      if (!(member in gate)) gate[member] = group.active;
    }
  }
  return gate;
}

/** The container containing a control key, if any. */
export function containerForControl(controlGroups: OverlayControlGroup[], key: string): OverlayControlGroup | null {
  return controlGroups.find((group) => group.members.includes(key)) ?? null;
}

/**
 * Effective on-air visibility per instance: visible AND container gate. Applied
 * to the config feeding OverlayStage (OBS page + designer Output panel) so
 * rendering agrees with effectiveOverlayControls / the program heartbeat.
 */
export function withEffectiveVisibility(config: OverlayConfig): OverlayConfig {
  if (config.controlGroups.length === 0) return config;
  const gate = controlGroupGateMap(config.controlGroups);
  const elements = { ...config.elements };
  for (const type of OVERLAY_ELEMENT_TYPES) {
    const key = OVERLAY_ELEMENT_TYPE_KEYS[type];
    elements[key] = (elements[key] as OverlayElementInstance[]).map((instance) => {
      const controlKey = overlayControlKey(type, instance.id);
      return gate[controlKey] === false ? { ...instance, visible: false } : instance;
    }) as never;
  }
  return { ...config, elements };
}

/**
 * Mirrors effectiveOverlayControls in functions/src/overlayRuntime.ts:
 * visible AND content AND container gate. Containers emit no keys of their own.
 */
export function effectiveOverlayControls(config: OverlayConfig): OverlayRuntimeControls {
  const gate = controlGroupGateMap(config.controlGroups);
  const controls: OverlayRuntimeControls = {};
  for (const instance of config.elements.gpsGroups) {
    controls[overlayControlKey("gps", instance.id)] = instance.visible;
  }
  for (const instance of config.elements.timers) {
    controls[overlayControlKey("timer", instance.id)] = instance.visible;
  }
  for (const instance of config.elements.pylons) {
    controls[overlayControlKey("pylon", instance.id)] =
      instance.visible && pylonAthleteRows(instance.rows).some((row) => !!row.person["full-name"].trim());
  }
  for (const instance of config.elements.gpsPylons) {
    controls[overlayControlKey("gpsPylon", instance.id)] =
      instance.visible && pylonAthleteRows(instance.rows).some((row) => !!row.person["full-name"].trim());
  }
  for (const instance of config.elements.sortFilterPylons) {
    controls[overlayControlKey("sortFilterPylon", instance.id)] =
      instance.visible && pylonAthleteRows(instance.rows).some((row) => !!row.person["full-name"].trim());
  }
  for (const instance of config.elements.lowerThirds) {
    controls[overlayControlKey("lowerThird", instance.id)] =
      instance.visible && !lowerThirdReadinessIssue(instance);
  }
  for (const instance of config.elements.customLowerThirds) {
    const selectedRow = instance.rows.find((row) => row.id === instance.selectedRowID);
    controls[overlayControlKey("customLowerThird", instance.id)] = instance.visible && !!selectedRow?.name.trim();
  }
  for (const instance of config.elements.playerCards) {
    controls[overlayControlKey("playerCard", instance.id)] =
      instance.visible && !!instance.person?.["full-name"].trim();
  }
  for (const instance of config.elements.scoreboards) {
    controls[overlayControlKey("scoreboard", instance.id)] = instance.visible;
  }
  for (const instance of config.elements.miniMaps) {
    controls[overlayControlKey("miniMap", instance.id)] = instance.visible;
  }
  for (const instance of config.elements.fullMaps) {
    controls[overlayControlKey("fullMap", instance.id)] = instance.visible;
  }
  for (const instance of config.elements.courseProfiles) {
    controls[overlayControlKey("courseProfile", instance.id)] = instance.visible;
  }
  for (const instance of config.elements.speedometers) {
    controls[overlayControlKey("speedometer", instance.id)] = instance.visible;
  }
  for (const instance of config.elements.gradeScopes) {
    controls[overlayControlKey("gradeScope", instance.id)] = instance.visible;
  }
  for (const instance of config.elements.customWidgets) {
    controls[overlayControlKey("customWidget", instance.id)] = instance.visible && instance.spec.layers.length > 0;
  }
  for (const instance of config.elements.bugs) {
    controls[overlayControlKey("bug", instance.id)] = instance.visible && !bugReadinessIssue(instance);
  }
  for (const instance of config.elements.compasses) {
    controls[overlayControlKey("compass", instance.id)] = instance.visible;
  }
  for (const instance of config.elements.billboards) {
    controls[overlayControlKey("billboard", instance.id)] =
      instance.visible && instance.rows.some((row) => !!row.url.trim() || !!row.message.trim());
  }
  for (const instance of config.elements.podiums) {
    controls[overlayControlKey("podium", instance.id)] =
      instance.visible && podiumVisibleSlots(instance).some((slot) => !!slot.person["full-name"].trim());
  }
  for (const instance of config.elements.imageCanvases) {
    controls[overlayControlKey("imageCanvas", instance.id)] = instance.visible && !!instance.url.trim();
  }
  for (const instance of config.elements.webSources) {
    controls[overlayControlKey("webSource", instance.id)] = instance.visible && !!normalizeWebSourceURL(instance.url);
  }
  for (const instance of config.elements.startLists) {
    controls[overlayControlKey("startList", instance.id)] =
      instance.visible && instance.rows.some((row) => !!row.person["full-name"].trim());
  }
  for (const instance of config.elements.tickers) {
    controls[overlayControlKey("ticker", instance.id)] = instance.visible && tickerHasContent(instance);
  }
  for (const instance of config.elements.advancedTimers) {
    controls[overlayControlKey("advancedTimer", instance.id)] = instance.visible && advancedTimerHasContent(instance);
  }
  for (const key of Object.keys(controls)) {
    if (gate[key] === false) controls[key] = false;
  }
  return controls;
}

/** The write response also carries what the transaction read before changing anything. */
export interface OverlayControlWriteResult extends OverlayRuntimeState {
  /** Raw (ungated) controls before this write; absent from functions deployed before it. */
  previousRawControls?: OverlayRuntimeControls;
  /** False when the flag already had its value and nothing was written. */
  changed?: boolean;
}

export async function setOverlayControl(
  overlayId: string,
  control: OverlayRuntimeControlKey,
  visible: boolean,
): Promise<OverlayControlWriteResult> {
  return apiRequest<OverlayControlWriteResult>(`/overlays/${encodeURIComponent(overlayId)}/controls`, {
    method: "POST",
    body: { control, visible },
  });
}

export async function getOverlayRuntimeState(overlayId: string): Promise<OverlayRuntimeState> {
  return apiRequest<OverlayRuntimeState>(`/overlays/${encodeURIComponent(overlayId)}/runtime-state`);
}

export function useOverlayRuntimeState(overlayId: string | undefined, intervalMs = 5_000) {
  const [state, setState] = useState<OverlayRuntimeState | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!overlayId) {
      setState(null);
      return;
    }
    let active = true;
    const refresh = async () => {
      try {
        const next = await getOverlayRuntimeState(overlayId);
        if (active) {
          setState(next);
          setError(null);
        }
      } catch (err) {
        if (active) setError(err instanceof Error ? err.message : "Could not refresh program state.");
      }
    };
    void refresh();
    const timer = window.setInterval(() => void refresh(), intervalMs);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [overlayId, intervalMs]);

  return { state, error, setState };
}

function programMode(): boolean {
  return new URLSearchParams(window.location.search).get("mode") === "program";
}

function programSessionID(overlayId: string): string {
  const key = `sbot-overlay-program-session:${overlayId}`;
  const existing = window.sessionStorage.getItem(key);
  if (existing) return existing;
  const generated = typeof crypto.randomUUID === "function"
    ? crypto.randomUUID().replace(/-/g, "")
    : `${Date.now()}${Math.random().toString(36).slice(2)}`;
  window.sessionStorage.setItem(key, generated);
  return generated;
}

/** Program pages acknowledge the state they can actually render; previews stay quiet. */
export function useProgramOverlayHeartbeat(overlayId: string | undefined, config: OverlayConfig | null | undefined): void {
  useEffect(() => {
    if (!overlayId || !config || !programMode()) return;
    const sessionID = programSessionID(overlayId);
    const heartbeat = () => {
      void fetch(`${apiBaseUrl}/overlay-runtime/${encodeURIComponent(overlayId)}/heartbeat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionID, controls: effectiveOverlayControls(config) }),
      });
    };
    heartbeat();
    const timer = window.setInterval(heartbeat, 5_000);
    return () => window.clearInterval(timer);
  }, [overlayId, config]);
}
